/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { createContext, useContext } from "react";

import { ApolloError } from "@apollo/client";
import {
  NotificationType,
  useNotificationListQuery,
} from "~/generated/graphql";
import { ACTIONS, useAccess } from "~/services/auth";

type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  details: string;
  targetView?: string | null;
  read: boolean;
  createdAt: string;
};

type INotificationProvider = {
  notificationsList: Notification[];
  loading: boolean | undefined;
  error: ApolloError | undefined;
  refetch: () => void;
  unread: number;
};

type IProps = {
  children: React.ReactNode;
};

const POLL_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

function createNotificationContext<ContextType>() {
  const context = createContext<ContextType | undefined>(undefined);
  context.displayName = "NotificationContext";

  function useNotificationContext() {
    const notificationContext = useContext(context);
    if (!notificationContext) {
      throw new Error(
        "NotificationContext must be inside a Provider with a value"
      );
    }
    return notificationContext;
  }

  return [useNotificationContext, context.Provider] as const;
}

const [useNotificationsAmount, NotificationContextProvider] =
  createNotificationContext<INotificationProvider>();

export { useNotificationsAmount };

export const notificationLimit = 20;

export function NotificationProvider({ children }: IProps) {
  const { checkAccess } = useAccess();
  const { data, loading, error, refetch } = useNotificationListQuery({
    variables: { cursor: "", limit: notificationLimit },
    pollInterval: POLL_INTERVAL_MS,
    skip: !checkAccess(ACTIONS.viewNotifications),
  });

  const notificationsList = data?.notifications?.notifications || [];

  const unread = notificationsList.filter(({ read }) => read === false).length;

  return (
    <NotificationContextProvider
      value={{ notificationsList, loading, error, refetch, unread }}
    >
      {children}
    </NotificationContextProvider>
  );
}
